import { useState, useCallback } from 'react';
import { unflattenIpcError } from '@volley/crucible-ipc-types/error-types';
import type { ConflictsResolveWithAiResult } from '@volley/crucible-ipc-types/ipc-channels';
import {
  usePendingConflicts,
  useResolveWithAi,
  useDismissConflicts,
} from '../hooks/useConflictResolution';

/**
 * Crucible #592 — `<ConflictResolutionCard>` is the renderer surface
 * for the agentic Resolve-with-AI flow. It watches
 * `<gameDir>/.foundry/pending-conflicts.json` via the
 * `conflicts:get-pending` IPC poll (cadence mirrors `<BugDraftCard>`).
 *
 * Mount: in the Workspace's input strip, above the chat input — same
 * band as `<BugDraftCard>` so pending-confirmation surfaces are
 * visually consolidated. The card auto-unmounts when the sidecar is
 * deleted (success path: main's PostToolBatch cleanup hook fires when
 * every listed file is marker-free; dismiss path: user clicked
 * "Resolve manually").
 *
 * Two CTAs:
 *
 *   - **Resolve with AI** — fires `conflicts:resolve-with-ai` with
 *     the file list. Main synthesises the orchestrator prompt and
 *     dispatches via `agentAdapter.sendMessage`. The resolution
 *     decisions surface in chat as ordinary assistant bubbles.
 *
 *   - **Resolve manually** — fires `conflicts:dismiss`; main deletes
 *     the sidecar; the card unmounts. User finishes via Cursor / VS
 *     Code as they did pre-#592.
 *
 * Renders nothing when the watcher returns null (no sidecar on disk).
 */
export interface ConflictResolutionCardProps {
  gameId: string;
}

export function ConflictResolutionCard({
  gameId,
}: ConflictResolutionCardProps): React.JSX.Element | null {
  const { data: sidecar } = usePendingConflicts(gameId);
  const { resolveWithAi } = useResolveWithAi();
  const { dismissConflicts } = useDismissConflicts();

  const [pending, setPending] = useState(false);
  const [errorState, setErrorState] = useState<ConflictsResolveWithAiResult | null>(null);

  const handleResolveWithAi = useCallback(async () => {
    if (!sidecar) return;
    setPending(true);
    setErrorState(null);
    try {
      const files = sidecar.files.map((f) => f.path);
      const result = await resolveWithAi({ gameId, files });
      if (!result.ok) {
        setErrorState(result);
      }
      // On success, the sidecar stays on disk until the agent
      // finishes (main's PostToolBatch hook deletes it). The card
      // stays mounted in the meantime so the user can see the
      // pending state; the 2s poll unmounts when the sidecar is
      // gone.
    } catch (err) {
      const decoded = unflattenIpcError(err);
      setErrorState({
        ok: false,
        errorKind: 'unexpected',
        error: decoded.message || 'Something went wrong dispatching the agent.',
      });
    } finally {
      setPending(false);
    }
  }, [gameId, resolveWithAi, sidecar]);

  const handleResolveManually = useCallback(async () => {
    setPending(true);
    setErrorState(null);
    try {
      const result = await dismissConflicts(gameId);
      if (!result.ok) {
        setErrorState({
          ok: false,
          errorKind: 'unexpected',
          error:
            'Could not delete the pending-conflicts sidecar. The card will refresh in a moment — if it persists, delete `.foundry/pending-conflicts.json` manually.',
        });
      }
    } catch (err) {
      const decoded = unflattenIpcError(err);
      setErrorState({
        ok: false,
        errorKind: 'unexpected',
        error:
          decoded.message ||
          'Something went wrong dismissing the conflict card.',
      });
    } finally {
      setPending(false);
    }
  }, [dismissConflicts, gameId]);

  if (!sidecar) return null;

  const fileCount = sidecar.files.length;

  return (
    <div
      data-testid="conflict-resolution-card"
      className="mb-3 rounded-lg border border-amber-500/30 bg-amber-500/5 p-4"
    >
      <div className="mb-2 flex items-center gap-2">
        <span aria-hidden="true" className="text-base">⚠️</span>
        <span
          data-testid="conflict-resolution-card-headline"
          className="text-sm font-medium text-amber-200"
        >
          {fileCount} {fileCount === 1 ? 'file has' : 'files have'} merge conflicts
        </span>
      </div>

      <p className="mb-2 text-xs text-amber-200/80">
        The template update from v{sidecar.templateFrom} → v{sidecar.templateTo} has
        unresolved differences in:
      </p>

      <ul
        data-testid="conflict-resolution-card-file-list"
        className="mb-3 max-h-32 overflow-auto rounded-md border border-amber-500/20 bg-black/20 px-2 py-1 font-mono text-[11px] text-amber-200/90"
      >
        {sidecar.files.map((f) => (
          <li key={f.path} data-testid={`conflict-resolution-card-file-${f.path}`}>
            • <span className="text-amber-100">{f.path}</span>{' '}
            <span className="text-amber-200/60">
              ({f.conflictBlocks} {f.conflictBlocks === 1 ? 'conflict block' : 'blocks'})
            </span>
          </li>
        ))}
      </ul>

      {errorState && !errorState.ok && (
        <div
          data-testid="conflict-resolution-card-error"
          className="mb-3 rounded border border-red-500/40 bg-red-500/10 p-2 text-xs text-red-200"
        >
          {errorState.error ?? 'Something went wrong.'}
        </div>
      )}

      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          data-testid="conflict-resolution-card-manual"
          onClick={() => {
            void handleResolveManually();
          }}
          disabled={pending}
          className="rounded border border-white/15 px-3 py-1 text-xs text-white/70 transition-colors hover:bg-white/5 disabled:opacity-50"
        >
          Resolve manually
        </button>
        <button
          type="button"
          data-testid="conflict-resolution-card-resolve"
          onClick={() => {
            void handleResolveWithAi();
          }}
          disabled={pending}
          className="rounded border border-amber-500/40 bg-amber-500/20 px-3 py-1 text-xs font-medium text-amber-100 transition-colors hover:bg-amber-500/30 disabled:opacity-50"
        >
          {pending ? 'Dispatching…' : 'Resolve with AI'}
        </button>
      </div>
    </div>
  );
}
